import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAIAuth } from '../lib/AIAuthContext';
import { formatSessionDate, groupSessions } from '../lib/groupSessions';
import type { ChatErrorCode, Session } from '../types/types.ts';
import { useChatStore } from './useChatStore';
import { useSessions } from './useSessions';

// Verbindet Liste, Suche und die Aktionen Öffnen und Löschen für die Verlauf-Ansicht
export function useChatHistory(query: string) {
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useAIAuth();
  const { sessions, error, isLoading, forget, remove } = useSessions();
  const loadSession = useChatStore((state) => state.loadSession);
  const closeHistory = useChatStore((state) => state.closeHistory);
  const activeSessionId = useChatStore((state) => state.sessionId);
  const isStreaming = useChatStore((state) => state.isStreaming);
  const isPending = useChatStore((state) => state.isPending);
  const [actionError, setActionError] = useState<ChatErrorCode>();
  const [pendingId, setPendingId] = useState<string>();

  const titleOf = (session: Session) =>
    session.titel ??
    t('domain.ai.chat.history.untitled', {
      date: formatSessionDate(session.createdAt, i18n.language),
    });

  const searchTerm = query.trim().toLowerCase();
  const groups = groupSessions(
    (sessions ?? []).filter((session) =>
      titleOf(session).toLowerCase().includes(searchTerm),
    ),
  );

  const open = async (session: Session) => {
    if (session.id === activeSessionId) {
      closeHistory();
      return;
    }
    setActionError(undefined);
    setPendingId(session.id);
    const code = await loadSession(session.id, { getAccessToken });
    if (code) {
      if (code === 'SESSION_NOT_FOUND') {
        forget(session.id);
      }
      setActionError(code);
      setPendingId(undefined);
    }
  };

  const removeSession = async (session: Session) => {
    setActionError(undefined);
    setPendingId(session.id);
    const code = await remove(session.id);
    setPendingId(undefined);
    if (code) {
      setActionError(code);
    }
  };

  return {
    hasLoaded: sessions !== undefined,
    isLoading,
    error: error ?? actionError,
    groups,
    isBusy: pendingId !== undefined,
    isStreaming: isStreaming || isPending,
    activeSessionId,
    titleOf,
    open,
    removeSession,
  };
}

export type ChatHistoryState = ReturnType<typeof useChatHistory>;
